import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "components/Common/PageHeader";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import useStudentTeacher from "../hooks/useStudentTeacher";
import useClassTeacher from "feature-module/academic/classes/hooks/useClassTeacher";
import { all_routes } from "router/all_routes";
import { GENDER_OPTIONS } from "constants/userConstants";
import TeacherStudentTable from "../table/TeacherStudentTable";
import { StudentType } from "types/studentTypes";
import CustomModal from "components/Common/CustomModal";
import StudentDetailsComponent from "../student-details/studentDetailsComponent";

const TeacherStudentsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedStudent, setSelectedStudent] = useState<StudentType | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const openStudentDetails = (student: StudentType) => {
        setSelectedStudent(student);
        setIsDetailModalOpen(true);
    };

    const closeStudentDetails = () => {
        setIsDetailModalOpen(false);
        setSelectedStudent(null);
    };

    const {
        students,
        isLoading,
        pagination,
        fetchStudentsTeacher,
        error,
    } = useStudentTeacher();

    const {
        classes,
        fetchTeacherClasses,
    } = useClassTeacher();

    const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

    const searchParams = new URLSearchParams(location.search);
    const classIdFromUrl = searchParams.get("classId");

    const translatedGender = GENDER_OPTIONS.map(gender => ({
        ...gender,
        label: t(gender.label)
    }));

    const classOptions = useMemo(() => {
        if (!classes || !Array.isArray(classes)) return [];
        return classes.map((item: any) => ({
            label: item.name,
            value: item.id
        }));
    }, [classes]);

    const filterFields: FilterField[] = useMemo(
        () => [
            { key: "firstName", label: t("first_name"), type: "text" },
            { key: "lastName", label: t("last_name"), type: "text" },
            { key: "email", label: t("email"), type: "text" },
            {
                key: "classId",
                label: t("class"),
                type: "select",
                options: classOptions
            },
            {
                key: "gender",
                label: t("gender"),
                type: "select",
                options: translatedGender
            },
        ],
        [t, classOptions, translatedGender]
    );

    useEffect(() => {
        const baseParams: any = {
            page: 1,
            limit: 10,
        };
        const initialParams = classIdFromUrl
            ? { ...baseParams, classId: classIdFromUrl }
            : baseParams;
        const initialFilter = { classId: classIdFromUrl ? classIdFromUrl : null }
        setFilters(initialFilter);
        fetchStudentsTeacher(initialParams);
        fetchTeacherClasses({
            page: 1,
            limit: 1000,
        });
    }, []);

    const removeClassIdFromUrl = () => {
        const params = new URLSearchParams(location.search);

        if (params.has("classId")) {
            params.delete("classId");
            const newSearch = params.toString();
            navigate(
                newSearch
                    ? `${location.pathname}?${newSearch}`
                    : location.pathname,
                { replace: true }
            );
        }
    };

    const handleFilterApply = (newFilters: { [key: string]: string | number | null }) => {
        const updatedFilters = {
            ...newFilters,
            page: 1,
            limit: pagination?.limit || 10,
        };

        setFilters(updatedFilters);
        removeClassIdFromUrl();
        fetchStudentsTeacher(updatedFilters);
    };

    const handleFilterReset = () => {
        const resetFilters = {
            page: 1,
            limit: pagination?.limit || 10,
        };

        setFilters({});
        removeClassIdFromUrl();
        fetchStudentsTeacher(resetFilters);
    };

    const handleTableChange = (pg: any) => {
        const newFilters = {
            ...filters,
            page: pg?.current || 1,
            limit: pg?.pageSize || 10,
        };

        fetchStudentsTeacher(newFilters);
    };

    const breadcrumbItems = [
        { label: t("dashboard"), path: all_routes.dashboard },
        { label: t("my_students"), active: true },
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <PageHeader
                    title={t("my_students")}
                    breadcrumbItems={breadcrumbItems}
                />

                <div className="d-flex mb-3 justify-content-end">
                    <TableFilterDynamique
                        fields={filterFields}
                        filters={filters}
                        onApply={handleFilterApply}
                        onReset={handleFilterReset}
                    />
                </div>

                <TeacherStudentTable
                    students={students}
                    isLoading={isLoading}
                    pagination={pagination}
                    error={error}
                    onChange={handleTableChange}
                    openStudentDetails={openStudentDetails}

                />

                <CustomModal
                    title={
                        selectedStudent
                            ? ([selectedStudent.user?.firstName, selectedStudent.user?.lastName]
                                .filter(Boolean)
                                .join(" ")
                                .trim() || t("student_details"))
                            : t("student_details")
                    }
                    isOpen={isDetailModalOpen}
                    onClose={closeStudentDetails}
                    width="60%"
                >
                    {selectedStudent && <StudentDetailsComponent student={selectedStudent} />}
                </CustomModal>

            </div>
        </div>
    );
};

export default TeacherStudentsPage;